import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import InfoTip from '../components/InfoTip';
import { BarChart3, RefreshCw, ExternalLink, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { navLabel } from '../utils/navigation';
import MonthPicker from '../components/MonthPicker';
import DetailsToggle from '../components/DetailsToggle';
import QuarterPicker from '../components/QuarterPicker';
import YearPicker from '../components/YearPicker';
import { apiService } from '../services/api';
import EpicSummaryPanel from '../components/epics/EpicSummaryPanel';
import DownloadMenu from '../components/DownloadMenu';
import StatusTimeline from '../components/epics/StatusTimeline';
import type { EpicTarget } from '../components/epics/types';
import {
  ALL_TABS,
  CATEGORY_TABS,
  EpicReportCounts,
  EpicReportMeta,
  EpicReportRow,
  EpicTabId,
  STATUS_TABS,
  StatusTabId,
  countFor,
  epicsToCsv,
  monthPeriodKey,
  quarterPeriodKey,
  reportFilename,
  tabLabel,
  yearPeriodKey,
} from '../utils/epicReport';
import { saveBlob, saveCsv } from '../utils/download';
import * as fmt from '../lib/format';
import { clampMonth, periodParam, quarterParam, yearParam } from '../utils/urlParams';
import { useTabParam } from '../hooks/useTabParam';
import { useUrlState } from '../hooks/useUrlState';
import { useDetails } from '../hooks/useDetails';

// The rows shown before "Show all": the list can run to a hundred epics, so a person opens on the newest change
// first and asks for the rest. The counts, the search and both downloads always use the whole matching list.
const ROWS_FIRST = 25;

// What a request is doing. A new request always starts from `loading`, so a list or a count is
// never shown under a period or tab it does not belong to.
type Load<T> = { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; message: string };

const errorText = (e: any, fallback: string): string => e?.response?.data?.error || e?.message || fallback;

// The four cards above the tabs are the status tabs, counted by the server like their badges.
const CARD_STYLE: Record<StatusTabId, { bg: string; headerText: string; valueText: string }> = {
  todo: { bg: 'bg-gray-50', headerText: 'text-gray-500', valueText: 'text-gray-700' },
  in_progress: { bg: 'bg-blue-50', headerText: 'text-blue-700', valueText: 'text-blue-700' },
  completed: { bg: 'bg-green-50', headerText: 'text-green-700', valueText: 'text-green-700' },
  on_hold: { bg: 'bg-red-50', headerText: 'text-red-700', valueText: 'text-red-700' },
};

// Calendar-quarter label for a year/month (month 1-12). Q1 = Jan-Mar; FY = last 2 digits.
function quarterLabel(year: number, month: number): string {
  const q = Math.floor((month - 1) / 3) + 1;
  return `Q${q} FY${String(year).slice(-2)}`;
}

// The issue name with the bracketed quarter tag (e.g. "[Q3 FY26]") removed — including the
// brackets — plus any leftover empty brackets and dangling separators.
function cleanTitle(projectName: string, issueName: string, quarterTag: string): string {
  const escaped = quarterTag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  let cleaned = (issueName || '')
    .replace(new RegExp(`\\[\\s*${escaped}\\s*\\]`, 'gi'), '') // the selected quarter, e.g. "[Q3 FY26]"
    .replace(/\[\s*Q[1-4]\s*FY\d{2}\s*\]/gi, '')                // any quarter tag (epics now travel across quarters)
    .replace(/\[\s*\]/g, '')                                    // leftover empty brackets
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s|\-–—]+|[\s|\-–—]+$/g, '')
    .trim();

  // If the project name is repeated in the issue name (e.g. "Saral (Misc) - DevOps..."),
  // drop everything up to and including the first "- " separator.
  if (projectName && cleaned.toLowerCase().includes(projectName.toLowerCase())) {
    const sep = cleaned.match(/[-–—]\s+/);
    if (sep && sep.index !== undefined) {
      cleaned = cleaned.slice(sep.index + sep[0].length).trim();
    }
  }

  return cleaned;
}

// "project_name | issue_name", cleaned as above.
function formatTask(projectName: string, issueName: string, quarterTag: string): string {
  return [projectName, cleanTitle(projectName, issueName, quarterTag)].filter(Boolean).join(' | ');
}

function currentMonthValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// Dynamic phase label based on how far into the current month we are.
// Past/other selected months are always a complete "Full month".
function reportPhase(selYear: number, selMonth: number): string {
  const now = new Date();
  const isCurrentMonth = selYear === now.getFullYear() && selMonth === now.getMonth() + 1;
  if (!isCurrentMonth) return 'Full month';
  const day = now.getDate();
  if (day <= 10) return 'Month start';
  if (day <= 20) return 'Mid-month';
  return 'Full month';
}

// ─── Quarter helpers (Q1 = Jan-Mar … Q4 = Oct-Dec) ────────────────────────────
function currentQuarterValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${Math.floor(now.getMonth() / 3) + 1}`;
}
function quarterPhase(selYear: number, selQuarter: number): string {
  const now = new Date();
  const curQuarter = Math.floor(now.getMonth() / 3) + 1;
  const isCurrent = selYear === now.getFullYear() && selQuarter === curQuarter;
  return isCurrent ? 'Quarter to date' : 'Full quarter';
}

// ─── Year helpers ─────────────────────────────────────────────────────────────
function currentYearValue(): string {
  return String(new Date().getFullYear());
}
function yearPhase(selYear: number): string {
  return selYear === new Date().getFullYear() ? 'Year to date' : 'Full year';
}

export default function ReportsOverviewPage() {
  // Report-kind tabs.
  const TABS = [
    { id: 'monthly', label: 'Monthly task report' },
    { id: 'quarterly', label: 'Quarterly task report' },
    { id: 'yearly', label: 'Yearly task report' },
  ] as const;
  // Everything the person picked lives in the address, so a reload or a copied link opens the same report:
  // ?report=quarterly&quarter=2026-Q3&tab=in_progress (the month is ?period=, the year ?year=).
  const { params, update } = useUrlState();
  const [activeTab, setActiveTab] = useTabParam<(typeof TABS)[number]['id']>(['monthly', 'quarterly', 'yearly'], 'monthly', 'report');
  const isQuarterly = activeTab === 'quarterly';
  const isYearly = activeTab === 'yearly';

  // The pickers hold a quarter as "2026-4"; the address writes it "2026-Q4". Nothing later than now is shown.
  const quarterNumber = (v: string) => { const [y, q] = v.split('-').map(Number); return y * 4 + q; };
  const monthValue = clampMonth(periodParam(params), currentMonthValue());
  const setMonthValue = (v: string) => update({ period: v >= currentMonthValue() ? null : v });
  const quarterInAddress = quarterParam(params)?.replace('-Q', '-') ?? null;
  const quarterValue = quarterInAddress && quarterNumber(quarterInAddress) <= quarterNumber(currentQuarterValue()) ? quarterInAddress : currentQuarterValue();
  const setQuarterValue = (v: string) => update({ quarter: v === currentQuarterValue() ? null : v.replace('-', '-Q') });
  const yearInAddress = yearParam(params);
  const yearValue = yearInAddress && Number(yearInAddress) <= Number(currentYearValue()) ? yearInAddress : currentYearValue();
  const setYearValue = (v: string) => update({ year: v === currentYearValue() ? null : v });

  // The period the server reads: "2026-10", "2026-Q4" or "2026". Every call below sends only this.
  const period = isYearly ? yearPeriodKey(yearValue) : isQuarterly ? quarterPeriodKey(quarterValue) : monthPeriodKey(monthValue);

  // Step the selected month by ±1, clamped to the current month.
  const shiftMonth = (delta: number) => {
    const [y, m] = monthValue.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (next <= currentMonthValue()) setMonthValue(next);
  };
  // Step the selected quarter by ±1 (wraps year), clamped to the current quarter.
  const shiftQuarter = (delta: number) => {
    const [y, q] = quarterValue.split('-').map(Number);
    let ny = y;
    let nq = q + delta;
    while (nq < 1) { nq += 4; ny -= 1; }
    while (nq > 4) { nq -= 4; ny += 1; }
    const [cy, cq] = currentQuarterValue().split('-').map(Number);
    if (ny < cy || (ny === cy && nq <= cq)) setQuarterValue(`${ny}-${nq}`);
  };
  // Step the selected year by ±1, clamped to the current year.
  const shiftYear = (delta: number) => {
    const next = parseInt(yearValue, 10) + delta;
    if (next <= parseInt(currentYearValue(), 10)) setYearValue(String(next));
  };

  // The tab that is open, and what the server said for it and for the period as a whole.
  const [tab, setTab] = useTabParam<EpicTabId>(ALL_TABS.map((t) => t.id), 'todo');
  // Headline first: the four status tabs. The same tasks by category are one press away (?details=1), and a
  // link to a category tab keeps that row in view so what is open is always on screen.
  const [details] = useDetails();
  // Rows: the first few, or all of them (?all=1; ?details=1 is the by-category row, a different choice).
  const [allRows, setAllRows] = useDetails('all');
  const categoryTab = CATEGORY_TABS.some((t) => t.id === tab);
  const categoryOpen = details || categoryTab;
  const visibleTabs = categoryOpen ? ALL_TABS : STATUS_TABS;
  const setCategoryOpen = (open: boolean) => update({ details: open ? '1' : null, ...(!open && categoryTab ? { tab: null } : {}) });
  const [attempt, setAttempt] = useState(0); // bumped by Refresh and Try again
  const [counts, setCounts] = useState<Load<{ counts: EpicReportCounts; meta: EpicReportMeta }>>({ status: 'loading' });
  const [list, setList] = useState<Load<EpicReportRow[]>>({ status: 'loading' });
  const [query, setQuery] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Epic summary drill-down. The target is kept after closing so the panel holds its content
  // while it slides away.
  const [epicTarget, setEpicTarget] = useState<EpicTarget | null>(null);
  const [epicPanelOpen, setEpicPanelOpen] = useState(false);

  // Counts for the cards, the badges and the total: one request per period. A newer request
  // (another period, Refresh) cancels what the older one would have shown.
  useEffect(() => {
    let cancelled = false;
    setCounts({ status: 'loading' });
    apiService
      .getEpicTabCounts(period)
      .then((res) => {
        if (cancelled) return;
        setCounts(
          res?.success
            ? { status: 'ready', data: { counts: res.data as EpicReportCounts, meta: res.meta as EpicReportMeta } }
            : { status: 'error', message: res?.error || 'Could not load the counts' }
        );
      })
      .catch((e: any) => {
        if (!cancelled) setCounts({ status: 'error', message: errorText(e, 'Could not load the counts') });
      });
    return () => {
      cancelled = true;
    };
  }, [period, attempt]);

  // The open tab's epics, newest change first.
  useEffect(() => {
    let cancelled = false;
    setList({ status: 'loading' });
    apiService
      .getEpicRoster(period, tab)
      .then((res) => {
        if (cancelled) return;
        setList(res?.success ? { status: 'ready', data: (res.data || []) as EpicReportRow[] } : { status: 'error', message: res?.error || 'Could not load the report' });
      })
      .catch((e: any) => {
        if (!cancelled) setList({ status: 'error', message: errorText(e, 'Could not load the report') });
      });
    return () => {
      cancelled = true;
    };
  }, [period, tab, attempt]);

  const loading = counts.status === 'loading' || list.status === 'loading';
  const countsData = counts.status === 'ready' ? counts.data.counts : null;
  const meta = counts.status === 'ready' ? counts.data.meta : null;
  const total = countsData ? countsData.total : null;
  const loadError = counts.status === 'error' ? counts.message : list.status === 'error' ? list.message : null;
  const problem = downloadError ?? loadError;

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const syncIndicator = React.useCallback(() => {
    const el = tabRefs.current[activeTab];
    if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
  }, [activeTab]);
  useLayoutEffect(syncIndicator, [syncIndicator]);
  useEffect(() => {
    window.addEventListener('resize', syncIndicator);
    return () => window.removeEventListener('resize', syncIndicator);
  }, [syncIndicator]);

  // Sliding underline for the status and category tabs. It sits under the active tab wherever that
  // tab is: the two groups share one row when they fit and wrap onto two rows when they do not.
  const taskTabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [taskIndicator, setTaskIndicator] = useState<{ left: number; top: number; width: number }>({ left: 0, top: 0, width: 0 });
  const syncTaskIndicator = React.useCallback(() => {
    const el = taskTabRefs.current[tab];
    if (el) setTaskIndicator({ left: el.offsetLeft, top: el.offsetTop + el.offsetHeight - 1, width: el.offsetWidth });
  }, [tab]);
  // The badges change width when the counts arrive, so the underline follows them too.
  useLayoutEffect(() => {
    syncTaskIndicator();
  }, [syncTaskIndicator, countsData, categoryOpen]);
  useEffect(() => {
    window.addEventListener('resize', syncTaskIndicator);
    return () => window.removeEventListener('resize', syncTaskIndicator);
  }, [syncTaskIndicator]);

  // Arrow keys move between the tabs, as a tab list should.
  const onTabKeyDown = (e: React.KeyboardEvent, id: EpicTabId) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const i = visibleTabs.findIndex((t) => t.id === id);
    const next =
      e.key === 'Home' ? 0 : e.key === 'End' ? visibleTabs.length - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + visibleTabs.length) % visibleTabs.length;
    setTab(visibleTabs[next].id);
    taskTabRefs.current[visibleTabs[next].id]?.focus();
  };

  const [selYear, selMonth] = monthValue.split('-').map((n) => parseInt(n, 10));
  const [qYear, qNum] = quarterValue.split('-').map((n) => parseInt(n, 10));
  // Quarter tag stripped from issue names, and the header labels — both adapt to the active report.
  const quarterTag = isYearly ? '' : isQuarterly ? `Q${qNum} FY${String(qYear).slice(-2)}` : quarterLabel(selYear, selMonth);
  const phase = isYearly ? yearPhase(parseInt(yearValue, 10)) : isQuarterly ? quarterPhase(qYear, qNum) : reportPhase(selYear, selMonth);
  const monthLabel = isYearly ? yearValue : isQuarterly ? fmt.quarter(`${qYear}-${qNum}`) : fmt.month(`${selYear}-${String(selMonth).padStart(2, '0')}`);
  const reportKind = isYearly ? 'yearly' : isQuarterly ? 'quarterly' : 'monthly';

  const openEpic = (r: EpicReportRow) => {
    // Finishing a text selection inside a row must not open the panel.
    if (window.getSelection?.()?.toString()) return;
    setEpicTarget({
      issueKey: r.issue_key,
      projectName: r.project_name ?? r.project_key,
      title: cleanTitle(r.project_name ?? '', r.issue_name ?? '', quarterTag),
      period,
      periodLabel: monthLabel,
    });
    setEpicPanelOpen(true);
  };

  // Precompute display fields + search index once per data change.
  const rows = list.status === 'ready' ? list.data : null;
  const decorated = useMemo(
    () =>
      (rows ?? []).map((r) => ({
        row: r,
        task: formatTask(r.project_name ?? '', r.issue_name ?? '', quarterTag),
      })),
    [rows, quarterTag]
  );

  const visibleTasks = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return decorated;
    return decorated.filter(({ row, task }) =>
      [row.issue_key, task, row.description, row.inside_line ?? '', row.project_name ?? '', row.status].some((v) => (v || '').toLowerCase().includes(q))
    );
  }, [decorated, query]);

  // What the table shows. Everything else (the badge counts, "Total", the search, the CSV of this tab) reads visibleTasks.
  const shownTasks = allRows ? visibleTasks : visibleTasks.slice(0, ROWS_FIRST);

  // The whole report: one real .xlsx from the server, so it holds every tab and the summary.
  const downloadWholeReport = async () => {
    setDownloadError(null);
    setDownloading(true);
    try {
      saveBlob(await apiService.downloadEpicReport(period), reportFilename(period, 'xlsx'));
    } catch (e: any) {
      setDownloadError(errorText(e, 'The download could not be created'));
    } finally {
      setDownloading(false);
    }
  };

  // The open tab as shown, with the search applied.
  const downloadTabCsv = () => {
    saveCsv(epicsToCsv(visibleTasks), reportFilename(period, 'csv', tab));
  };

  const retry = () => {
    setDownloadError(null);
    setAttempt((a) => a + 1);
  };

  const periodNoun = reportKind === 'yearly' ? 'year' : reportKind === 'quarterly' ? 'quarter' : 'month';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60">
      <PageHeader title={navLabel('/reports-overview')} subtitle="Project delivery reports" icon={BarChart3} />

      <div className="p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Report tabs */}
          <div className="relative flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto">
            {TABS.map((t) => {
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  ref={(el) => { tabRefs.current[t.id] = el; }}
                  onClick={() => setActiveTab(t.id)}
                  className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${active ? 'text-blue-700' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  {t.label}
                </button>
              );
            })}
            <span
              className="pointer-events-none absolute bottom-0 h-0.5 rounded-full bg-blue-600 transition-all duration-300 ease-out motion-reduce:transition-none"
              style={{ left: indicator.left, width: indicator.width }}
            />
          </div>

          {/* Title */}
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2.5">
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">{monthLabel}</h1>
                <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                  {phase}
                </span>
              </div>
              <p className="mt-0.5 min-h-[1.75rem] text-md text-gray-500">
                Total <span className="font-bold text-lg tabular-nums text-gray-600">{total ?? '–'}</span> {total === 1 ? 'Task' : 'Tasks'}
                {meta && <span className="ml-2 text-sm text-gray-500">· status as at {meta.as_at_label}</span>}
                <InfoTip id="reports.roster" />
              </p>
            </div>

            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => (isYearly ? shiftYear(-1) : isQuarterly ? shiftQuarter(-1) : shiftMonth(-1))}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title={`Previous ${periodNoun}`}
                  aria-label={`Previous ${periodNoun}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {isYearly ? (
                  <YearPicker value={yearValue} max={currentYearValue()} onChange={setYearValue} />
                ) : isQuarterly ? (
                  <QuarterPicker value={quarterValue} max={currentQuarterValue()} onChange={setQuarterValue} />
                ) : (
                  <MonthPicker value={monthValue} max={currentMonthValue()} onChange={setMonthValue} />
                )}
                <button
                  onClick={() => (isYearly ? shiftYear(1) : isQuarterly ? shiftQuarter(1) : shiftMonth(1))}
                  disabled={isYearly ? yearValue >= currentYearValue() : isQuarterly ? quarterValue >= currentQuarterValue() : monthValue >= currentMonthValue()}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                  title={`Next ${periodNoun}`}
                  aria-label={`Next ${periodNoun}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Download: the whole report as one Excel file, or the open tab as CSV */}
              <DownloadMenu
                busy={downloading}
                disabled={total === null}
                items={[
                  {
                    key: 'xlsx',
                    title: 'Whole report (.xlsx)',
                    hint: `Every tab and a summary, status as at ${meta?.as_at_label ?? 'the end of the period'}`,
                    disabled: total === 0,
                    onSelect: downloadWholeReport,
                  },
                  {
                    key: 'csv',
                    title: 'This tab (.csv)',
                    hint: `${tabLabel(tab)}: ${visibleTasks.length} ${visibleTasks.length === 1 ? 'task' : 'tasks'}${query.trim() ? ' matching your search' : ''}`,
                    disabled: visibleTasks.length === 0,
                    onSelect: downloadTabCsv,
                  },
                ]}
              />

              <button
                onClick={retry}
                disabled={loading}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-700 active:scale-[0.98] disabled:opacity-50 motion-reduce:transition-none motion-reduce:active:scale-100"
                title="Refresh"
                aria-label="Refresh"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {problem && (
            <div role="alert" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <span>{downloadError ? `The download could not be created: ${downloadError}` : `The report could not be loaded: ${problem}`}</span>
              <button
                type="button"
                onClick={retry}
                className="rounded-lg bg-white px-3 py-1 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
              >
                Try again
              </button>
            </div>
          )}

          {/* Status summary: the four status tabs, counted the same way as their badges */}
          <div className="mb-4 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed border-collapse text-center text-sm [&_td]:border [&_td]:border-gray-200 [&_th]:border [&_th]:border-gray-200">
                <thead>
                  <tr className="text-[0.6875rem] font-semibold uppercase tracking-normal sm:text-[0.6875rem] sm:tracking-wider">
                    {STATUS_TABS.map((c) => (
                      <th key={c.id} className={`px-1.5 py-3 align-middle sm:px-4 ${CARD_STYLE[c.id].bg} ${CARD_STYLE[c.id].headerText}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {STATUS_TABS.map((c) => (
                      <td key={c.id} className={`px-1.5 py-3 align-middle sm:px-4 text-lg font-semibold tabular-nums ${CARD_STYLE[c.id].valueText}`}>
                        {countsData ? countFor(countsData, c.id) : '–'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Search */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] max-w-md flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search task, key, description…"
                aria-label="Search tasks"
                className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <DetailsToggle className="ml-auto" open={categoryOpen} onChange={setCategoryOpen} showLabel="Show by category" hideLabel="Hide by category" />
          </div>

          {/* Task tabs — the same tasks read two ways: by status and by category. Each way adds up to the total. */}
          {/* On a phone the two rows of tabs are wider than the screen: the strip scrolls sideways (the underline moves with it). */}
          <div className="relative mb-3 border-b border-gray-200 max-md:overflow-x-auto">
            <div className="flex flex-wrap items-end gap-x-8">
              {([
                { caption: 'By status', tabs: STATUS_TABS },
                { caption: 'By category', tabs: CATEGORY_TABS },
              ] as const).filter((group) => group.caption === 'By status' || categoryOpen).map((group, gi) => (
                <React.Fragment key={group.caption}>
                  {gi > 0 && <div className="self-stretch border-l border-gray-200" />}
                  <div>
                    <div className="mb-1 flex items-center pl-1 text-[0.6875rem] font-semibold uppercase tracking-widest text-gray-500">
                      {group.caption}
                      {group.caption === 'By category' && <InfoTip id="reports.by-category" />}
                    </div>
                    <div className="flex items-center" role="tablist" aria-label={group.caption}>
                      {group.tabs.map((t) => {
                        const active = tab === t.id;
                        return (
                          <button
                            key={t.id}
                            id={`epic-tab-${t.id}`}
                            ref={(el) => { taskTabRefs.current[t.id] = el; }}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            aria-controls="epic-tab-panel"
                            tabIndex={active ? 0 : -1}
                            onClick={() => setTab(t.id)}
                            onKeyDown={(e) => onTabKeyDown(e, t.id)}
                            className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap px-3 py-2.5 text-center text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400 motion-reduce:transition-none ${
                              active ? 'text-blue-700' : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            {t.label}
                            <span className={`inline-block min-w-[1.5rem] rounded-full px-1.5 py-0.5 text-center text-[0.6875rem] font-semibold tabular-nums ${
                              active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {countsData ? countFor(countsData, t.id) : '–'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <span
              aria-hidden
              className="pointer-events-none absolute h-0.5 rounded-full bg-blue-600 transition-all duration-300 ease-out motion-reduce:transition-none"
              style={{ left: taskIndicator.left, top: taskIndicator.top, width: taskIndicator.width }}
            />
          </div>

          {/* Table card */}
          <div id="epic-tab-panel" role="tabpanel" aria-labelledby={`epic-tab-${tab}`} className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
            <div className="max-h-[75dvh] overflow-auto md:max-h-[calc(100vh-16rem)]">
              {/* On a phone the table scrolls sideways inside its card and the Task column stays pinned, so a row is never separated from its task. */}
              <table className="w-full min-w-[44rem] table-fixed border-collapse text-left md:min-w-0 [&_td]:border [&_td]:border-gray-200 [&_td]:align-middle [&_th]:border [&_th]:border-gray-200">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-gray-50/95 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500 backdrop-blur">
                    <th className="sticky left-0 z-30 w-44 bg-gray-50 px-3 py-3.5 sm:px-5 md:static md:w-72 md:bg-transparent">Task</th>
                    <th className="px-5 py-3.5"><span className="inline-flex items-center">Description<InfoTip id="reports.description" /></span></th>
                    <th className="px-5 py-3.5 w-56 md:w-64"><span className="inline-flex items-center">Status history<InfoTip id="reports.history" /></span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {list.status === 'loading' ? (
                    <tr>
                      <td colSpan={3} className="py-16 text-center">
                        <RefreshCw className="mx-auto h-6 w-6 animate-spin text-gray-300" aria-label="Loading" />
                      </td>
                    </tr>
                  ) : list.status === 'error' ? (
                    <tr>
                      <td colSpan={3} className="py-16 text-center text-sm text-gray-500">
                        The {tabLabel(tab)} list could not be loaded.
                      </td>
                    </tr>
                  ) : visibleTasks.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-16 text-center text-sm text-gray-500">
                        {query
                          ? `No ${tabLabel(tab)} tasks match your search.`
                          : `No ${tabLabel(tab)} tasks found for ${monthLabel}.`}
                      </td>
                    </tr>
                  ) : (
                    shownTasks.map(({ row: r, task }) => (
                      <tr
                        key={r.issue_id}
                        onClick={() => openEpic(r)}
                        className="cursor-pointer align-top transition-colors odd:bg-white even:bg-gray-50/40 hover:bg-blue-50/40 active:bg-blue-100/40 motion-reduce:transition-none"
                      >
                        {/* Task */}
                        <td className="sticky left-0 z-10 bg-white px-3 py-4 sm:px-5 md:static md:bg-transparent">
                          <a
                            href={`https://saral-tracker.atlassian.net/browse/${r.issue_key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hit-area-sm inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-200/60 transition-colors hover:bg-blue-100"
                          >
                            {r.issue_key}
                            <ExternalLink className="h-3 w-3 opacity-60" />
                          </a>
                          {/* The whole row opens the summary; this button is its keyboard and screen-reader handle. */}
                          <button
                            type="button"
                            aria-label={`Open summary for ${r.issue_key}`}
                            className="mt-1.5 block min-h-6 w-full rounded text-left font-semibold leading-snug text-gray-900 transition-colors hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                          >
                            {task}
                          </button>
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                            {r.priority && (
                              <span className="text-[0.6875rem] font-medium text-gray-500">{r.priority} priority</span>
                            )}
                            <span className="inline-flex items-center gap-0.5 text-[0.6875rem] font-medium text-blue-600">
                              View summary
                              <ChevronRight className="h-3 w-3" aria-hidden />
                            </span>
                          </div>
                        </td>

                        {/* Description. An epic whose description is blank, or the same as several others, shows
                            what is inside it instead, so a bucket is not described by boilerplate. */}
                        <td className="px-5 py-4">
                          {r.description_kind === 'own' && r.description ? (
                            <p className="line-clamp-5 text-sm leading-relaxed text-gray-600" title={r.description}>
                              {r.description}
                            </p>
                          ) : r.inside_line ? (
                            <div>
                              <span className="block text-[0.6875rem] font-semibold uppercase tracking-wide text-gray-500">What's inside</span>
                              <p className="mt-0.5 text-sm leading-relaxed text-gray-600">{r.inside_line}</p>
                            </div>
                          ) : (
                            <span className="text-gray-300" aria-hidden>—</span>
                          )}
                        </td>

                        {/* Status history, oldest to newest */}
                        <td className="px-5 py-4">
                          <StatusTimeline steps={r.status_timeline} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {visibleTasks.length > ROWS_FIRST && (
            <div className="mt-3">
              <DetailsToggle
                open={allRows}
                onChange={setAllRows}
                showLabel={`Show all ${fmt.count(visibleTasks.length)} tasks`}
                hideLabel={`Show only the first ${ROWS_FIRST}`}
                controls="epic-tab-panel"
              />
            </div>
          )}
        </div>
      </div>

      <EpicSummaryPanel isOpen={epicPanelOpen} target={epicTarget} onClose={() => setEpicPanelOpen(false)} />
    </div>
  );
}
